import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import { getMe } from '../services/usersService';

const UserContext = createContext(null);

export const useUser = () => useContext(UserContext);

export const UserProvider = ({ children }) => {
  const auth = useAuth();
  const sub = auth.isAuthenticated ? auth.user?.profile?.sub : null;
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);

  // Refs mantêm fetchUserData estável e compartilham a requisição em andamento,
  // evitando chamadas duplicadas ao /me (guards + páginas montando juntos).
  const userDataRef = useRef(null);
  const inFlightRef = useRef(null);
  const subRef = useRef(sub);
  subRef.current = sub;

  // Busca /me. Nunca rejeita: retorna null em caso de falha (ver `error`).
  const fetchUserData = useCallback(async (force = false) => {
    if (!subRef.current) return null;
    if (userDataRef.current && !force) return userDataRef.current;
    if (inFlightRef.current) return inFlightRef.current;

    setLoading(true);
    setError(null);

    inFlightRef.current = getMe()
      .then((data) => {
        userDataRef.current = data;
        setUserData(data);
        setInitialized(true);
        return data;
      })
      .catch((err) => {
        console.error('Error fetching user data:', err);
        setError(err);
        return null;
      })
      .finally(() => {
        inFlightRef.current = null;
        setLoading(false);
      });

    return inFlightRef.current;
  }, []);

  const updateUserData = useCallback((newData) => {
    setUserData(prev => {
      const next = { ...prev, ...newData };
      userDataRef.current = next;
      return next;
    });
  }, []);

  // Carrega os dados ao autenticar (ou trocar de usuário); limpa ao deslogar.
  // Renovações de token mantêm o mesmo `sub` e não disparam nova busca.
  useEffect(() => {
    userDataRef.current = null;
    setUserData(null);
    setInitialized(false);
    setError(null);
    if (sub) fetchUserData();
  }, [sub, fetchUserData]);

  const value = {
    user: userData,
    loading,
    error,
    fetchUserData,
    updateUserData,
    initialized
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};
